import { z } from "zod";
import type { FinancialYear } from "../types.js";

// ---------------------------------------------------------------------------
// side-income — the side-hustle / gig tax surface. Three calculator faces over
// one small FY-versioned constants table plus the shared `income-tax` engine:
//
//   • setAside     — the EXTRA tax a salaried person owes on expected ABN
//                    income, computed by MARGINAL STACKING through the
//                    income-tax engine (tax(salary+abn) − tax(salary)), turned
//                    into a per-invoice set-aside % and the two next-year
//                    auto-enrolment flags (PAYG instalments, GST trip-wire).
//   • gstThreshold — the $75,000 GST-registration test on a rolling 12-month
//                    window (current OR projected), the 21-day deadline, the
//                    retrospective-GST-on-late-registration warning, and the
//                    voluntary-registration trade-off.
//   • rideshareGst — the ride-sourcing carve-out: ride-sourcing is taxi travel
//                    for GST, so GST applies from the FIRST dollar (no $75k);
//                    food delivery keeps the $75k threshold; any ride-sourcing
//                    in a mixed activity forces GST across the whole ABN.
//
// Like income-tax/deduction this is NOT a per-product (au0x) ruleset — it has
// no `product` code. Its rulesets carry an `engine: "side-income"` tag and the
// usual provenance fields. Calculation is pure: inputs -> rules -> outputs, the
// assessment FY is always passed in, no Date.now(), no I/O.
//
// REGULATORY POSTURE (binding — validation-plan §3): this surface outputs
// information, not advice. Every flag states the cited ATO RULE and what it
// MEANS for the user's figures — never "you should register" / "you should
// start instalments". The set-aside % is framed as an estimate. No financial
// product and no paid financial-product handoff ever attaches to a result; the
// affiliate payout is UNVERIFIED (ledger Fig 10) and is NOT baked into any calc.
// ---------------------------------------------------------------------------

/** PAYG-instalment auto-entry thresholds (ATO "Starting PAYG instalments").
 * An individual / sole trader is auto-entered when BOTH legs are met on the
 * latest return + Notice of Assessment. */
export interface PaygInstalmentRules {
  /** Instalment income (gross business + investment income, excl. GST and
   * capital gains) at/above which the income leg is met (e.g. $4,000). */
  instalmentIncomeThreshold: number;
  /** Tax payable on the latest Notice of Assessment at/above which the tax leg
   * is met (e.g. $1,000). */
  taxOnNoaThreshold: number;
  /** GDP adjustment factor the ATO applies to the prior-year base for the
   * instalment amount (e.g. 0.04 = 4% for FY2025-26; 5% for FY2026-27). Carried
   * for completeness/forward-currency; the v1 set-aside surface uses the flag
   * only, not the GDP-uplifted instalment amount. */
  gdpAdjustmentFactor: number;
}

/** GST-registration rules (ATO "Registering for GST"). */
export interface GstRegistrationRules {
  /** Standard registration trip-wire on GST turnover (current OR projected
   * rolling 12-month), e.g. $75,000. */
  turnoverThreshold: number;
  /** Days after meeting/expecting the threshold within which you must register
   * (e.g. 21). */
  registrationDeadlineDays: number;
  /** GST rate (e.g. 0.10 = 10%). */
  gstRate: number;
  /** Divisor that extracts the GST component from a GST-INCLUSIVE amount
   * (e.g. 11 → GST = amount ÷ 11 = 1/11 ≈ 9.09% of the inclusive price). */
  gstInclusiveDivisor: number;
}

/** A complete FY side-income ruleset, keyed by financial year. */
export interface SideIncomeRuleSet {
  /** Engine tag — distinguishes shared engines from per-product rulesets. */
  engine: "side-income";
  /** Financial-year key, e.g. "2025-26". */
  fy: FinancialYear;
  /** Inclusive FY start (1 July). */
  effectiveFrom: string;
  /** Inclusive FY end (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
  /** PAYG-instalment auto-entry thresholds + GDP factor. */
  paygInstalments: PaygInstalmentRules;
  /** GST-registration trip-wire, deadline, rate, and 1/11 divisor. */
  gst: GstRegistrationRules;
}

// ---------------------------------------------------------------------------
// setAside — extra tax on expected ABN income, marginally stacked on salary.
// ---------------------------------------------------------------------------

export const SetAsideInputSchema = z.object({
  /** PAYG salary/wage taxable income already taxed at source for the FY. The
   * side income stacks ON TOP of this, so the extra tax is taxed at the
   * salary's marginal rate, not in isolation. Pass 0 for a no-salary user. */
  salary: z.number().min(0),
  /** Expected NET ABN / side income for the FY (gross side income minus the
   * user's self-estimated deductible expenses). This is what the extra tax is
   * computed on; deductions are user-supplied and the output is an estimate. */
  abnIncome: z.number().min(0),
  /** Whether the user has an outstanding HELP/STSL loan. When true the extra
   * also includes the increase in the compulsory HELP repayment caused by the
   * side income (it lifts repayment income). Defaults to false. */
  hasStudyLoan: z.boolean().default(false),
  /** Prior-year tax payable on the latest Notice of Assessment, if known. Used
   * only to evaluate the PAYG-instalment tax leg (≥ $1,000). When omitted we
   * fall back to this year's estimated total tax as a proxy and say so. */
  priorYearTaxOnNoa: z.number().min(0).optional(),
  /** The financial year being assessed, e.g. "2025-26". Passed in (never from
   * the clock) so historical re-runs resolve against the right ruleset. */
  assessmentYear: z.string(),
});
export type SetAsideInput = z.input<typeof SetAsideInputSchema>;
export type SetAsideParsed = z.infer<typeof SetAsideInputSchema>;

/** The two next-year auto-enrolment flags, each stated as a cited rule. */
export interface AutoEnrolFlags {
  /** PAYG instalments: the ATO auto-enters an individual/sole trader when
   * instalment income ≥ threshold AND tax on the latest NoA ≥ threshold. We
   * report whether BOTH legs are met (and each leg) — as a fact, not a "you
   * should". */
  paygInstalments: {
    /** Both legs met → the ATO rule auto-enrols you next year. */
    likely: boolean;
    /** Income leg: instalment income ≥ instalmentIncomeThreshold. */
    incomeLegMet: boolean;
    /** Tax leg: tax on the latest NoA ≥ taxOnNoaThreshold. */
    taxLegMet: boolean;
    /** Whether the tax leg used the prior-year NoA (true) or fell back to this
     * year's estimated total tax as a proxy (false). */
    usedPriorYearNoa: boolean;
  };
  /** GST trip-wire: side income alone meeting the $75k turnover threshold.
   * Reported as a flag here; the gstThreshold face does the full rolling test. */
  gstRegistration: {
    /** Expected ABN income ≥ the GST turnover threshold. */
    likely: boolean;
    /** The threshold tested against. */
    threshold: number;
  };
}

export interface SetAsideOutput {
  /** PAYG salary the stacking ran against. */
  salary: number;
  /** Net ABN income the extra tax was computed on. */
  abnIncome: number;
  /** Total tax (income tax + Medicare levy + any HELP repayment) on salary
   * alone — the baseline. */
  taxOnSalary: number;
  /** Total tax on salary + ABN income combined. */
  taxOnCombined: number;
  /** EXTRA tax attributable to the side income = combined − salary baseline.
   * This is the marginally-stacked figure (never the side income taxed in
   * isolation). Whole dollars, never negative. */
  extraTax: number;
  /** The marginal income-tax rate the NEXT dollar of side income is taxed at
   * (the stacking rate; excludes Medicare levy and HELP). */
  marginalRate: number;
  /** Suggested per-invoice set-aside %, as a fraction of side income. Rounded
   * UP to a clean step so the user is never left short (conservative framing).
   * 0 when there is no side income. */
  setAsidePct: number;
  /** The raw extra-tax / ABN-income ratio before the conservative round-up, for
   * transparency. null when abnIncome is 0. */
  effectiveRate: number | null;
  /** The two next-year auto-enrolment flags, each a cited rule. */
  flags: AutoEnrolFlags;
}

// ---------------------------------------------------------------------------
// gstThreshold — the $75k rolling-12-month registration test.
// ---------------------------------------------------------------------------

export const GstThresholdInputSchema = z
  .object({
    /** Current GST turnover: gross business income for the current month and
     * the previous 11 months (ATO definition). Gross, not profit; excludes GST.
     * Supply this directly, OR supply `monthlyTurnover` to have us sum it. */
    currentTurnover: z.number().min(0).optional(),
    /** Projected GST turnover: gross business income for the current month and
     * the NEXT 11 months (ATO definition). Supply directly or via
     * `projectedMonthlyTurnover`. */
    projectedTurnover: z.number().min(0).optional(),
    /** Optional: the trailing 12 months of gross turnover (current month +
     * previous 11). Summed into `currentTurnover` when present. */
    monthlyTurnover: z.array(z.number().min(0)).optional(),
    /** Optional: the forward 12 months (current month + next 11). Summed into
     * `projectedTurnover` when present. */
    projectedMonthlyTurnover: z.array(z.number().min(0)).optional(),
    /** Optional: business purchases on which input credits could be reclaimed
     * if registered (GST-inclusive). Drives the voluntary trade-off estimate. */
    annualBusinessPurchases: z.number().min(0).default(0),
    /** The financial year being assessed, e.g. "2025-26". */
    assessmentYear: z.string(),
  })
  .refine(
    (v) =>
      v.currentTurnover !== undefined || v.monthlyTurnover !== undefined,
    "supply currentTurnover or monthlyTurnover",
  )
  .refine(
    (v) =>
      v.projectedTurnover !== undefined ||
      v.projectedMonthlyTurnover !== undefined,
    "supply projectedTurnover or projectedMonthlyTurnover",
  );
export type GstThresholdInput = z.input<typeof GstThresholdInputSchema>;
export type GstThresholdParsed = z.infer<typeof GstThresholdInputSchema>;

/** The neutral, two-column voluntary-registration trade-off. By design it does
 * NOT resolve to a "worth it / not worth it" verdict — the choice is the
 * user's (regulatory ship-condition: no recommendation row). */
export interface VoluntaryTradeOff {
  /** Estimated input tax credits reclaimable on business purchases = purchases
   * ÷ the 1/11 divisor. Whole dollars. */
  inputCreditEstimate: number;
  /** The +% you would ADD to your ex-GST price if you registered (= gstRate,
   * e.g. 0.10 = +10%). */
  priceAddOnPct: number;
  /** The GST you would remit as a share of the NEW tax-inclusive price
   * (= 1/divisor ≈ 0.0909). Stated separately from priceAddOnPct so the two
   * bases are never blurred (ledger Fig 9). */
  inclusivePriceSharePct: number;
}

export interface GstThresholdOutput {
  /** Current GST turnover used (current month + previous 11). */
  currentTurnover: number;
  /** Projected GST turnover used (current month + next 11). */
  projectedTurnover: number;
  /** The $75k threshold tested against. */
  threshold: number;
  /** Current leg meets/exceeds the threshold. */
  currentMeets: boolean;
  /** Projected leg meets/exceeds the threshold. */
  projectedMeets: boolean;
  /** Either leg meets the threshold → registration is required under the ATO
   * test on the figures supplied. Stated as a test result, not a legal
   * determination. */
  registrationRequired: boolean;
  /** Days to register after the threshold is met (e.g. 21). */
  registrationDeadlineDays: number;
  /** Retrospective-GST warning is live (i.e. required AND the user indicates a
   * past crossing) — always surfaced as a cited factual risk when required. */
  retrospectiveRisk: boolean;
  /** The neutral voluntary-registration trade-off (relevant when NOT required;
   * always computed so copy can show it under the threshold). */
  voluntary: VoluntaryTradeOff;
}

// ---------------------------------------------------------------------------
// rideshareGst — the ride-sourcing GST-from-$0 carve-out.
// ---------------------------------------------------------------------------

export const RideshareGstInputSchema = z.object({
  /** Whether any ride-sourcing (Uber/Didi/Ola passenger trips) is performed.
   * Ride-sourcing is taxi travel for GST → GST from the first dollar. */
  doesRideSourcing: z.boolean(),
  /** Whether any food delivery (Uber Eats/DoorDash/Menulog) is performed.
   * Food delivery is NOT taxi travel → standard $75k threshold. */
  doesFoodDelivery: z.boolean().default(false),
  /** Whether any other (non-ride-sourcing) gig activity is performed — tasking,
   * asset hire, etc. Treated like delivery for the threshold (standard $75k). */
  doesOtherGig: z.boolean().default(false),
  /** Expected total gross turnover across all activities for the FY, used for
   * the $75k test on the NON-ride-sourcing path. */
  expectedTurnover: z.number().min(0).default(0),
  /** The financial year being assessed, e.g. "2025-26". */
  assessmentYear: z.string(),
});
export type RideshareGstInput = z.input<typeof RideshareGstInputSchema>;
export type RideshareGstParsed = z.infer<typeof RideshareGstInputSchema>;

export interface RideshareGstOutput {
  /** GST registration is mandatory. True whenever ride-sourcing is present
   * (GST from $1, no threshold) OR the $75k turnover test is met. */
  gstRequired: boolean;
  /** Why GST is required (or not): the bright-line reason, for cited copy. */
  reason:
    | "ride-sourcing-from-first-dollar"
    | "turnover-threshold-met"
    | "below-threshold-no-rideshare";
  /** Ride-sourcing forces GST from the FIRST dollar with no $75k threshold. */
  gstFromFirstDollar: boolean;
  /** Any ride-sourcing present in a mixed activity forces GST across the WHOLE
   * ABN, including the food-delivery / other-gig income. */
  mixedActivityForcesGst: boolean;
  /** The $75k threshold that WOULD apply to a pure non-ride-sourcing activity. */
  thresholdIfNoRideSourcing: number;
  /** Whether the $75k test is met on `expectedTurnover` (relevant only on the
   * no-ride-sourcing path). */
  turnoverThresholdMet: boolean;
  /** SERR note applies: the platform reports your income to the ATO twice a
   * year (cited fact, never a scare). */
  serrReportingApplies: boolean;
}
